import React, { useState } from 'react';
import MuiTabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';

export interface TabsProps {
  activeKey?: React.Key;
  defaultActiveKey?: React.Key;
  onChange?: (key: React.Key) => void;
  style?: React.CSSProperties;
  className?: string;
  children?: React.ReactNode;
  type?: 'line' | 'card' | 'editable-card';
  tabPosition?: 'top' | 'bottom' | 'left' | 'right';
  size?: 'large' | 'middle' | 'small';
  [key: string]: any;
}

export interface TabPaneProps {
  tab: React.ReactNode
  /** Ant-style tab key — dùng khi không muốn dựa vào React `key`. */
  tabKey?: React.Key
  key?: React.Key
  children?: React.ReactNode
  disabled?: boolean
  forceRender?: boolean
  [key: string]: any
}

const TabPane: React.FC<TabPaneProps> = ({ children }) => <>{children}</>;

function collectPanes(children: React.ReactNode): TabPaneProps[] {
  const panes: TabPaneProps[] = []
  React.Children.forEach(children, (child, index) => {
    if (!React.isValidElement(child)) return
    if (child.type === TabPane || (child.type as any)?.name === 'TabPane') {
      const props = child.props as TabPaneProps
      panes.push({
        ...props,
        // React không đưa `key` vào props — lấy từ element.key
        key: child.key != null && child.key !== '.' ? child.key : props.tabKey ?? String(index),
      })
    }
  })
  return panes
}

export const Tabs: React.FC<TabsProps> & { TabPane: typeof TabPane } = ({
  activeKey,
  defaultActiveKey,
  onChange,
  style,
  className,
  children,
  tabPosition = 'top',
}) => {
  const panes = collectPanes(children)
  const keys = panes.map((p) => p.key)
  const fallbackKey = keys[0] ?? '0'
  const [innerKey, setInnerKey] = useState<React.Key>(defaultActiveKey ?? fallbackKey)
  const current = activeKey ?? innerKey
  const index = Math.max(
    0,
    keys.findIndex((k) => k === current || String(k) === String(current)),
  )

  // Khi danh sách tab đổi (permission), giữ tab hợp lệ
  React.useEffect(() => {
    if (activeKey !== undefined) return
    if (!keys.length) return
    if (!keys.some((k) => k === innerKey || String(k) === String(innerKey))) {
      setInnerKey(keys[0])
    }
  }, [activeKey, innerKey, keys.join('|')])

  return (
    <Box className={className} sx={style}>
      <MuiTabs
        value={index}
        orientation={tabPosition === 'left' || tabPosition === 'right' ? 'vertical' : 'horizontal'}
        onChange={(_, idx) => {
          const k = keys[idx];
          if (activeKey === undefined) setInnerKey(k);
          onChange?.(k);
        }}
      >
        {panes.map((p) => (
          <Tab key={p.key} label={p.tab} disabled={p.disabled} id={`tab-${p.key}`} />
        ))}
      </MuiTabs>
      <Box role="tabpanel" sx={{ pt: 2 }}>
        {panes.map((p, i) => {
          if (!p.forceRender && i !== index) return null;
          return (
            <Box key={p.key} hidden={i !== index}>
              {p.children}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

Tabs.TabPane = TabPane;

export default Tabs;
